import React, { useEffect, useRef } from 'react';
import { PASSWORD_RE } from '../utils/valueFormats';
import { redirect, useSearchParams, useNavigate, useLocation, Link as RouterLink } from 'react-router-dom';
import { useState } from 'react';
import {
    Container, Typography, FormControl, FormGroup, FormControlLabel, Tooltip, Table, TableBody, TableCell, Link,
    Checkbox, InputLabel, Select, MenuItem, RadioGroup, Radio, Button, Fade, TableContainer, TableHead, Snackbar,
    useMediaQuery, Grid, TextField, Autocomplete, Chip, CircularProgress, DialogContentText, TableRow,
    Backdrop, FormHelperText, Paper, Divider, List, ListItem, ListItemText, LinearProgress, Alert, InputAdornment,
    ListItemButton, ListItemIcon, Box, Dialog, DialogTitle, DialogContent, DialogActions, GlobalStyles
} from '@mui/material';
import fetchData from '../utils/fetchData';
import TurnStile from '../components/TurnStile';
import { auth } from '../config/firebase';
import {
    getAuth,
    verifyPasswordResetCode,
    confirmPasswordReset,
    applyActionCode
} from "firebase/auth";
import logo_no_tagline from "../assets/logo_no_tagline.jpg";
import { notifySeverity } from '../utils/snackbarSeverity';

/*
 * These links are opened from a mail app, mostly on a phone. A line of red text
 * with nothing under it was a dead end there — the only way on was editing the
 * address bar — so a bad link always offers the way to sign in.
 */
const BadLink = ({ children }) => (
    <Container sx={{ textAlign: "center", pt: 10 }}>
        <Typography variant="h6" color="error">
            {children}
        </Typography>
        <Button component={RouterLink} to="/" variant="contained" sx={{ mt: 3, textTransform: "none", borderRadius: 2, px: 3 }}>
            Go to sign in
        </Button>
    </Container>
);

const Verification = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();

    const mode = searchParams.get("mode");
    const oobCode = searchParams.get("oobCode");

    if (!mode || !oobCode) {
        return <BadLink>Invalid or missing verification link.</BadLink>;
    }

    // ✅ Render correct section based on mode
    switch (mode) {
        case "resetPassword":
            return <ResetPassword oobCode={oobCode} />;
        case "verifyEmail":
            return <VerifyEmail oobCode={oobCode} />;
        default:
            return <BadLink>Unsupported verification mode: {mode}</BadLink>;
    }
};

export default Verification;

const ResetPassword = ({ oobCode }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const [loading, setLoading] = useState(true);
    const [showForm, setShowForm] = useState(true);
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [passwordError, setPasswordError] = useState();
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [passwordFocused, setPasswordFocused] = useState(false);
    const [searchParams] = useSearchParams();
    // const [oobCode, setOobCode] = useState('');
    const [validCode, setValidCode] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const hasVerifiedForReset = useRef(false);

    // The same rule as sign-up (utils/valueFormats): a symbol allowlist refused the
    // passwords phone password managers generate for this very field.
    const validatePassword = (password) => PASSWORD_RE.test(password);

    useEffect(() => {
        if (hasVerifiedForReset.current) return;
        hasVerifiedForReset.current = true;
        if (!oobCode) {
            setSnackbarMessage("Invalid or missing reset code.");
            setSnackbarOpen(true);
            setShowForm(false);
            return;
        }

        const auth = getAuth();
        verifyPasswordResetCode(auth, oobCode)
            .then((emailFound) => {
                setEmail(emailFound);
                setValidCode(true);
                setShowForm(true);
            })
            .catch((err) => {
                console.error("Invalid reset code:", err);
                setSnackbarMessage("This link is no longer valid or has expired.You will be redirect to Forget Password page.");
                setSnackbarOpen(true);
                setShowForm(false);
                setTimeout(() => navigate("/forget-password"), 3000);
            });

    }, [auth, oobCode]);

    const resetPassword = async (event) => {
        event.preventDefault();
        setPasswordError("");
        setSnackbarMessage("");
        setSnackbarOpen(false);

        if (!password) {
            setPasswordError('Password is required');
            return;
        } else if (!validatePassword(password)) {
            setPasswordError('Password must be at least 6 characters, include letters and numbers');
            return;
        }

        const auth = getAuth();
        setSubmitting(true);

        try {
            await confirmPasswordReset(auth, oobCode, password);
            setSnackbarMessage('Your password has been reset successfully. You will be redirect to login.');
            setSnackbarOpen(true);
            setTimeout(() => navigate("/"), 3000);
        } catch (err) {
            console.error(err);
            setSnackbarMessage("Failed to reset password. Please try again.");
            setSnackbarOpen(true);
        } finally {
            setSubmitting(false);
        }

    };


    return (
        <Container maxWidth="md">
            <Container
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'flex-start',
                    pt: { xs: 3, md: 12 },
                    alignItems: 'center',
                    // min, not fixed: a taller form (a phone in landscape, the
                    // keyboard up) grows the page instead of overflowing it.
                    minHeight: '100vh',
                    textAlign: 'center',
                    px: 2,
                }}
            >
                <Box
                    component="img"
                    src={logo_no_tagline}
                    alt="Logo"
                    sx={{
                        width: { xs: '120px', sm: '140px', md: '180px', lg: '200px' },
                        height: 'auto',
                    }}
                />

                <Box
                    component="form"
                    noValidate
                    onSubmit={resetPassword}
                    sx={{
                        width: 400,
                        maxWidth: { xs: "90%", sm: "90%", lg: "400px" },
                        backgroundColor: 'var(--surface)',
                        px: { xs: 2, sm: 3, lg: 4 },
                        py: { xs: 3, sm: 3, lg: 4 },
                        borderRadius: 3,
                        boxShadow: 3
                    }}
                >
                    <Box sx={{ mb: 3 }}>
                        <Typography variant="h5" component="h1" gutterBottom sx={{ fontWeight: 'bold' }}>
                            Reset Password
                        </Typography>
                    </Box>
                    {showForm ? (
                        <>
                            {/* Email Field */}
                            {email && (
                                <Typography sx={{ mb: 3, marginLeft: 0.5, color: "text.secondary", textAlign: "left" }}>
                                    Resetting password for: <strong>{email}</strong>
                                </Typography>
                            )}
                            {/* New Password Field. "new-password" makes a phone's
                                password manager offer a strong password and save it;
                                the hidden username says WHICH saved login it updates. */}
                            {email && <input type="email" autoComplete="username" value={email} readOnly hidden />}
                            <TextField
                                label="New Password"
                                name="password"
                                type="password"
                                autoComplete="new-password"
                                value={password}
                                onChange={(e) => {
                                    setPassword(e.target.value);
                                    setPasswordError("");
                                }}
                                // onFocus={() => setEmailFocused(true)}
                                // onBlur={() => setEmailFocused(false)}
                                InputLabelProps={{ shrink: true }}
                                fullWidth
                                error={!!passwordError}
                                helperText={passwordError}
                                sx={{ mb: 2 }}
                            />

                            {/* Send Reset Button */}
                            <Button
                                type="submit"
                                variant="contained"
                                size="large"
                                fullWidth
                                disabled={submitting}
                                sx={{ textTransform: "none", borderRadius: 2, py: 1.2 }}
                            >
                                {submitting ? <CircularProgress size={24} color="inherit" /> : "Reset"}
                            </Button>
                        </>
                    ) : (
                        <CircularProgress size={24} color="inherit" />
                    )}

                    {/* Divider with text */}
                    <Divider sx={{ my: 4 }} />

                    {/* Login Section */}
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                        }}
                    >
                        <Typography variant="body2" color="text.secondary">
                            Already reset your password?
                        </Typography>
                        <Button
                            component={RouterLink}
                            to="/"
                            variant="outlined"
                            color="primary"
                            size="medium"
                            sx={{
                                textTransform: "none",
                                borderRadius: 2,
                                ml: 2,
                                px: 3,
                            }}
                        >
                            Login
                        </Button>
                    </Box>
                </Box>
            </Container>
            <Snackbar
                open={snackbarOpen}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                sx={{ mt: 5, mr: 1 }}
                onClose={() => setSnackbarOpen(false)}
            // message={snackbarMessage}
            >
                <Alert onClose={() => setSnackbarOpen(false)} severity={notifySeverity(snackbarMessage)} sx={{ width: '100%', fontSize: '1.0rem' }}>
                    {snackbarMessage}
                </Alert>
            </Snackbar>
        </Container>
    )
}

const VerifyEmail = ({ oobCode }) => {
    const hasVerified = useRef(false);
    const navigate = useNavigate();
    const [message, setMessage] = useState("Verifying your email...");
    const [success, setSuccess] = useState(false);
    const [failed, setFailed] = useState(false); // success starts false too, so it can't tell pending from failed
    const auth = getAuth();

    useEffect(() => {
        if (hasVerified.current) return;
        hasVerified.current = true;
        applyActionCode(auth, oobCode)
            .then(() => {
                setMessage("Email verified successfully! You will be redirect to login.");
                setSuccess(true);
                setTimeout(() => {
                    navigate("/");
                }, 3000);
            })
            .catch((error) => {
                console.error("Verification error:", error);
                if (error.code === "auth/invalid-action-code") {
                    if (auth.currentUser?.emailVerified) {
                        setMessage("Email is already verified. You can log in now.");
                    } else {
                        setMessage("Invalid or expired verification link. Please request a new one.");
                    }
                } else {
                    setMessage("Something went wrong during verification.");
                }
                setSuccess(false);
                setFailed(true);
            });

    }, [auth, oobCode]);

    return (
        <Container maxWidth="md">
            <Container
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'flex-start',
                    pt: { xs: 3, md: 12 },
                    alignItems: 'center',
                    // min, not fixed: a taller form (a phone in landscape, the
                    // keyboard up) grows the page instead of overflowing it.
                    minHeight: '100vh',
                    textAlign: 'center',
                    px: 2,
                }}
            >
                <Box
                    component="img"
                    src={logo_no_tagline}
                    alt="Logo"
                    sx={{
                        width: { xs: '120px', sm: '140px', md: '160px', lg: '180px' },
                        height: 'auto',
                    }}
                />

                <Box
                    sx={{
                        width: 400,
                        maxWidth: { xs: "90%", sm: "90%", lg: "400px" },
                        backgroundColor: 'var(--surface)',
                        px: { xs: 2, sm: 3, lg: 4 },
                        py: { xs: 3, sm: 3, lg: 4 },
                        borderRadius: 3,
                        boxShadow: 3
                    }}
                >
                    <Box sx={{ mb: 3 }}>
                        <Typography variant="h5" component="h1" gutterBottom sx={{ fontWeight: 'bold' }}>
                            Verification Result
                        </Typography>
                    </Box>

                    <Box
                        sx={{
                            alignItems: "center"
                        }}
                    >
                        <Typography
                            variant="body"
                            color="text.secondary"
                        >
                            {message}
                        </Typography>
                    </Box>
                    {/* An expired or failed link left only the message above —
                        a dead end in a phone's mail app. (Success redirects.) */}
                    {failed && (
                        <Button component={RouterLink} to="/" variant="contained" sx={{ mt: 3, textTransform: "none", borderRadius: 2, px: 3 }}>
                            Go to sign in
                        </Button>
                    )}
                </Box>
            </Container>
        </Container>
    );
};